<script setup>
import { router } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  traces: Object,
  stats: Object,
  modelTypes: Array,
  filters: Object,
})

const expandedRows = ref({})
const selectedFrom = ref(props.filters?.from ? new Date(props.filters.from) : null)
const selectedTo = ref(props.filters?.to ? new Date(props.filters.to) : null)
const selectedModelType = ref(props.filters?.model_type || null)

// Apply filters
const applyFilters = () => {
  router.get(route('admin.prediction-traces.index'), {
    from: selectedFrom.value ? formatDate(selectedFrom.value) : null,
    to: selectedTo.value ? formatDate(selectedTo.value) : null,
    model_type: selectedModelType.value || null,
  }, {
    preserveState: true,
    preserveScroll: true,
  })
}

const clearFilters = () => {
  selectedFrom.value = null
  selectedTo.value = null
  selectedModelType.value = null
  router.get(route('admin.prediction-traces.index'), {}, {
    preserveState: true,
    preserveScroll: true,
  })
}

const formatDate = (date) => {
  if (!date) return null
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  const d = new Date(dateString)
  return d.toLocaleString('et-EE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatPrice = (value) => {
  if (!value) return '-'
  return new Intl.NumberFormat('et-EE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const formatMs = (ms) => {
  if (!ms && ms !== 0) return '-'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

const formatErrorPct = (trace) => {
  if (!trace.actual_price || !trace.predicted_value) return '-'
  const error = ((trace.predicted_value - trace.actual_price) / trace.actual_price) * 100
  const sign = error >= 0 ? '+' : ''
  return `${sign}${error.toFixed(1)}%`
}

const getErrorClass = (trace) => {
  if (!trace.actual_price || !trace.predicted_value) return ''
  const error = Math.abs(((trace.predicted_value - trace.actual_price) / trace.actual_price) * 100)
  if (error <= 5) return 'text-green-600'
  if (error <= 10) return 'text-yellow-600'
  if (error <= 15) return 'text-orange-500'
  return 'text-red-600'
}

// Model types for dropdown
const modelTypeOptions = computed(() => {
  return [
    { label: 'Kõik', value: null },
    ...props.modelTypes.map(type => ({ label: type, value: type }))
  ]
})

// Phase timing helpers
const getPhaseColor = (phaseName) => {
  const colors = {
    feature_coercion: 'bg-blue-100 text-blue-800',
    ebm_prediction: 'bg-green-100 text-green-800',
    similarity_search: 'bg-purple-100 text-purple-800',
    contribution_analysis: 'bg-yellow-100 text-yellow-800',
    difference_calculation: 'bg-pink-100 text-pink-800',
    group_aggregation: 'bg-indigo-100 text-indigo-800',
    weight_calculation: 'bg-teal-100 text-teal-800',
    final_aggregation: 'bg-orange-100 text-orange-800',
  }
  return colors[phaseName] || 'bg-gray-100 text-gray-800'
}

const getPhaseLabel = (phaseName) => {
  const labels = {
    feature_coercion: 'Feature Coercion',
    ebm_prediction: 'EBM Prediction',
    similarity_search: 'Similarity Search',
    contribution_analysis: 'Contribution Analysis',
    difference_calculation: 'Difference Calc',
    group_aggregation: 'Group Aggregation',
    weight_calculation: 'Weight Calculation',
    final_aggregation: 'Final Aggregation',
  }
  return labels[phaseName] || phaseName
}
</script>

<template>
  <div class="p-4">
    <h2 class="text-xl font-semibold mb-4">ML API Ennustused</h2>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Today Count -->
      <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
        <div class="text-sm text-gray-500 mb-1">Täna tehtud</div>
        <div class="text-2xl font-bold text-gray-800">{{ stats.today_count }}</div>
        <div class="text-xs text-gray-400 mt-1">ennustust</div>
      </div>

      <!-- Avg Latency -->
      <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
        <div class="text-sm text-gray-500 mb-1">Kesk. latentsus</div>
        <div class="text-2xl font-bold text-gray-800">{{ formatMs(stats.avg_latency_ms) }}</div>
        <div class="text-xs text-gray-400 mt-1">täna</div>
      </div>

      <!-- Matched Count -->
      <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-500">
        <div class="text-sm text-gray-500 mb-1">Võrreldud</div>
        <div class="text-2xl font-bold text-gray-800">{{ stats.matched_count }}</div>
        <div class="text-xs text-gray-400 mt-1">tegelike tehingutega</div>
      </div>

      <!-- Mean Error -->
      <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-orange-500">
        <div class="text-sm text-gray-500 mb-1">Kesk. viga</div>
        <div class="text-2xl font-bold" :class="stats.mean_error_pct > 10 ? 'text-red-600' : stats.mean_error_pct > 5 ? 'text-yellow-600' : 'text-green-600'">
          {{ stats.mean_error_pct ? `${stats.mean_error_pct.toFixed(1)}%` : '-' }}
        </div>
        <div class="text-xs text-gray-400 mt-1">võrreldud ennustustel</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-sm p-4 mb-4">
      <div class="flex flex-wrap items-end gap-4">
        <div class="flex flex-col">
          <label class="text-sm text-gray-600 mb-1">Alates</label>
          <Calendar
            v-model="selectedFrom"
            dateFormat="dd.mm.yy"
            showIcon
            :showButtonBar="true"
            @date-select="applyFilters"
          />
        </div>
        <div class="flex flex-col">
          <label class="text-sm text-gray-600 mb-1">Kuni</label>
          <Calendar
            v-model="selectedTo"
            dateFormat="dd.mm.yy"
            showIcon
            :showButtonBar="true"
            @date-select="applyFilters"
          />
        </div>
        <div class="flex flex-col">
          <label class="text-sm text-gray-600 mb-1">Mudeli tüüp</label>
          <Select
            v-model="selectedModelType"
            :options="modelTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Vali mudel"
            class="w-64"
            @change="applyFilters"
          />
        </div>
        <Button
          v-if="selectedFrom || selectedTo || selectedModelType"
          severity="secondary"
          outlined
          size="small"
          @click="clearFilters"
        >
          Tühjenda filtrid
        </Button>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white rounded-lg shadow-sm">
      <DataTable
        v-model:expandedRows="expandedRows"
        :value="traces.data"
        :paginator="false"
        removableSort
        hover
        stripedRows
        dataKey="id"
      >
        <Column expander style="width: 3rem" />

        <Column sortable field="created_at" header="Aeg">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.created_at) }}
          </template>
        </Column>

        <Column sortable field="evaluation_id" header="Hindamine">
          <template #body="slotProps">
            <Link
              v-if="slotProps.data.evaluation_id"
              :href="`/evaluation/${slotProps.data.evaluation_id}`"
              class="text-blue-600 hover:text-blue-800 hover:underline"
            >
              #{{ slotProps.data.evaluation_id }}
            </Link>
            <span v-else class="text-gray-400">-</span>
          </template>
        </Column>

        <Column field="user" header="Kasutaja">
          <template #body="slotProps">
            <span v-if="slotProps.data.user">
              {{ slotProps.data.user.firstname }} {{ slotProps.data.user.lastname }}
            </span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </Column>

        <Column sortable field="model_type" header="Mudel">
          <template #body="slotProps">
            <span class="text-xs font-mono truncate max-w-[200px] inline-block" :title="slotProps.data.model_type">
              {{ slotProps.data.model_type || '-' }}
            </span>
          </template>
        </Column>

        <Column sortable field="predicted_value" header="Ennustus">
          <template #body="slotProps">
            {{ formatPrice(slotProps.data.predicted_value) }}
          </template>
        </Column>

        <Column field="actual_price" header="Tegelik">
          <template #body="slotProps">
            <span v-if="slotProps.data.actual_price">
              {{ formatPrice(slotProps.data.actual_price) }}
            </span>
            <span v-else class="text-gray-400 text-xs">pole võrreldud</span>
          </template>
        </Column>

        <Column header="Viga">
          <template #body="slotProps">
            <span :class="getErrorClass(slotProps.data)" class="font-medium">
              {{ formatErrorPct(slotProps.data) }}
            </span>
          </template>
        </Column>

        <Column sortable field="total_duration_ms" header="Latentsus">
          <template #body="slotProps">
            {{ formatMs(slotProps.data.total_duration_ms) }}
          </template>
        </Column>

        <Column sortable field="n_comparables" header="Võrdl.">
          <template #body="slotProps">
            {{ slotProps.data.n_comparables }}
          </template>
        </Column>

        <!-- Expanded Row Template for Phase Timings -->
        <template #expansion="slotProps">
          <div class="p-4 bg-gray-50">
            <h4 class="font-semibold mb-3 text-gray-700">Faasid</h4>
            <div v-if="slotProps.data.phase_timings && slotProps.data.phase_timings.length > 0" class="flex flex-wrap gap-2">
              <div
                v-for="phase in slotProps.data.phase_timings"
                :key="phase.name"
                :class="getPhaseColor(phase.name)"
                class="px-3 py-1.5 rounded-full text-sm font-medium"
              >
                {{ getPhaseLabel(phase.name) }}:
                <span class="font-bold">{{ formatMs(phase.duration_ms) }}</span>
              </div>
            </div>
            <div v-else class="text-gray-500 text-sm">
              Faasideta andmeid pole saadaval
            </div>

            <!-- Additional trace info -->
            <div class="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Request ID:</span>
                <span class="ml-1 font-mono text-xs">{{ slotProps.data.request_id || '-' }}</span>
              </div>
              <div>
                <span class="text-gray-500">Keskkond:</span>
                <span class="ml-1">{{ slotProps.data.environment || '-' }}</span>
              </div>
              <div>
                <span class="text-gray-500">API versioon:</span>
                <span class="ml-1">{{ slotProps.data.api_version || '-' }}</span>
              </div>
              <div>
                <span class="text-gray-500">Kesk. võrdl. hind:</span>
                <span class="ml-1">{{ formatPrice(slotProps.data.comparable_average) }}</span>
              </div>
            </div>

            <!-- Matched transaction info -->
            <div v-if="slotProps.data.matched_at" class="mt-4 p-3 bg-green-50 rounded-sm border border-green-200">
              <span class="text-green-700 font-medium">Võrreldud:</span>
              <span class="ml-2 text-sm text-green-600">
                {{ formatDateTime(slotProps.data.matched_at) }}
              </span>
            </div>
          </div>
        </template>
      </DataTable>

      <!-- Pagination -->
      <div v-if="traces.links && traces.links.length > 3" class="p-4 border-t flex items-center justify-between">
        <div class="text-sm text-gray-600">
          Näitan {{ traces.from }}-{{ traces.to }} / {{ traces.total }}
        </div>
        <div class="flex gap-1">
          <template v-for="link in traces.links" :key="link.label">
            <Link
              v-if="link.url"
              :href="link.url"
              class="px-3 py-1 rounded-sm text-sm"
              :class="link.active ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'"
              v-html="link.label"
              preserve-scroll
            />
            <span
              v-else
              class="px-3 py-1 rounded-sm text-sm text-gray-400"
              v-html="link.label"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
